import React from "react";
import { connect } from "react-redux";

import Dialog from "../dialog/Dialog";
import DialogClose from "../dialog/DialogClose";
import DialogContent from "../dialog/DialogContent";
import DialogFooter from "../dialog/DialogFooter";
import DialogOK from "../dialog/DialogOK";
import Panel from "../panels/Panel";
import FontMenu from "./FontMenu";
import { Font, Margin } from "../../types/system/PrintConfiguration";
import ApplicationState from "../../foundation/AppState";
import {
    setAddColumns,
    setColWidth,
    setMargin,
    setNoColumns,
    setPageOrientation,
    setSchoolFont,
    setSeparatePages,
    setTableFont,
    setTableHeadFont,
} from "../../state/system/UIConfiguration";
import { IAppState } from "../../state/Store";
import Checkbox from "../inputform/Checkbox";
import Rendering from "../../foundation/Rendering";
import PreviewDialog from "./PreviewDialog";

import docIcon from "../../graphics/doc.svg";
import xlsIcon from "../../graphics/xls.svg";
import pdfIcon from "../../graphics/pdf.svg";

import "./PrintMenu.scss";

interface PrintMenuProps {
    open: boolean;
    disableDOC: boolean;
    disableXLS: boolean;
    disablePDF: boolean;
    printClosed: Function;

    schoolFont: Font;
    tableHeadFont: Font;
    tableFont: Font;
    pageOrientation: number;
    margin: Margin;
    addColumns: boolean;
    noColumns: number;
    colWidth: number;
    separatePages: boolean;

    setSchoolFont: (font: Font) => void;
    setTableHeadFont: (font: Font) => void;
    setTableFont: (font: Font) => void;
    setMargin: (margin: Margin) => void;
    setAddColumns: (addColumns: boolean) => void;
    setNoColumns: (noColumns: number) => void;
    setColWidth: (colWidth: number) => void;
    setSeparatePages: (separatePages: boolean) => void;
    setPageOrientation: (orientation: number) => void;
}

interface PrintMenuState {
    isOpen: boolean;
    isLoading: boolean;
    fontMenuOpen: boolean;
    previewDialogOpen: boolean;
    previewData: string;
    editingFont: Font;
    editingFontType: "schoolFont" | "tableHeadFont" | "tableFont";
}

class PrintMenu extends React.Component<PrintMenuProps, PrintMenuState> {
    state = {
        isOpen: false,
        isLoading: false,
        fontMenuOpen: false,
        previewDialogOpen: false,
        previewData: "",
        editingFont: null,
        editingFontType: null,
    };

    componentDidMount(): void {}

    setIsOpen(value: boolean) {
        this.setState(() => ({
            isOpen: value,
        }));
    }

    setIsLoading(value: boolean) {
        this.setState(() => ({
            isLoading: value,
        }));
    }

    chooseFont(
        font: Font,
        fontType: "schoolFont" | "tableHeadFont" | "tableFont",
    ) {
        this.setState({
            fontMenuOpen: true,
            editingFont: font,
            editingFontType: fontType,
        });
    }

    async preview() {
        this.setIsLoading(true);

        const data = await Rendering.preview();
        this.setState({
            previewData: data,
            previewDialogOpen: true,
        });

        this.setIsLoading(false);
    }

    async exportXLS() {
        this.setIsLoading(true);

        await Rendering.exportXLS();

        this.setIsLoading(false);
    }

    async exportDOC() {
        this.setIsLoading(true);

        await Rendering.exportDOC();

        this.setIsLoading(false);
    }

    async exportPDF() {
        this.setIsLoading(true);

        await Rendering.exportPDF();

        this.setIsLoading(false);
    }

    async printPDF() {
        this.setIsLoading(true);

        await Rendering.doPrint();

        this.setIsLoading(false);
    }

    addOrientationChanged(value: number) {
        this.props.setPageOrientation(value);
    }

    marginTopChanged(value: number) {
        this.props.setMargin({ ...this.props.margin, top: value });
    }

    marginLeftChanged(value: number) {
        this.props.setMargin({ ...this.props.margin, left: value });
    }

    marginBottomChanged(value: number) {
        this.props.setMargin({ ...this.props.margin, bottom: value });
    }

    marginRightChanged(value: number) {
        this.props.setMargin({ ...this.props.margin, right: value });
    }

    addColsChanged() {
        this.props.setAddColumns(!this.props.addColumns);
    }

    noColumnsChanged(value: number) {
        this.props.setNoColumns(value);
    }

    colWidthChanged(value: number) {
        this.props.setColWidth(value);
    }

    separatePagesChanged() {
        this.props.setSeparatePages(!this.props.separatePages);
    }

    closeFontMenu() {
        this.setState({ editingFont: null, fontMenuOpen: false });
    }

    closePreview() {
        this.setState({ previewDialogOpen: false });
    }

    updateFont(font: Font) {
        switch (this.state.editingFontType) {
            case "schoolFont":
                this.props.setSchoolFont(font);
                break;
            case "tableHeadFont":
                this.props.setTableHeadFont(font);
                break;
            case "tableFont":
                this.props.setTableFont(font);
                break;
        }
        ApplicationState.instance.storeState();
        this.setState({ editingFont: null, fontMenuOpen: false });
    }

    render(): React.ReactNode {
        const css = `
            .school-name-font {
                font-family: ${this.props.schoolFont?.name};
                font-size: ${this.props.schoolFont?.size}pt;
                font-weight: ${this.props.schoolFont?.weight};
                font-style: ${this.props.schoolFont?.decoration};
            }
            .table-head-font {
                font-family: ${this.props.tableHeadFont?.name};
                font-size: ${this.props.tableHeadFont?.size}pt;
                font-weight: ${this.props.tableHeadFont?.weight};
                font-style: ${this.props.tableHeadFont?.decoration};
            }
            .table-body-font {
                font-family: ${this.props.tableFont?.name};
                font-size: ${this.props.tableFont?.size}pt;
                font-weight: ${this.props.tableFont?.weight};
                font-style: ${this.props.tableFont?.decoration};
            }
        `;
        return (
            <>
                <Dialog
                    dialogTitle="Print Options"
                    dialogIcon="print"
                    showDialog={this.props.open}
                    showLoading={this.state.isLoading}
                    canResize={false}
                    width={670}
                    zIndex={999}>
                    <DialogContent className="print-dialog">
                        <style>{css}</style>
                        <div className="print-dialog">
                            <div className="col">
                                <Panel className="buffer">
                                    <h3>Printer Font Settings</h3>

                                    <div>
                                        <div className="top-clearance-small">
                                            <button
                                                className="actionbutton onethird no-image right-clearance-small"
                                                onClick={() =>
                                                    this.chooseFont(
                                                        this.props.schoolFont,
                                                        "schoolFont",
                                                    )
                                                }>
                                                <span>School Name</span>
                                            </button>

                                            <span className="font-example school-name-font">
                                                {this.props.schoolFont?.name}{" "}
                                                {this.props.schoolFont?.size}{" "}
                                                {this.props.schoolFont
                                                    ?.weight === 500
                                                    ? ""
                                                    : "Bold"}{" "}
                                                {this.props.schoolFont
                                                    ?.decoration === "normal"
                                                    ? ""
                                                    : "Italic"}
                                            </span>
                                        </div>
                                        <div className="top-clearance-small">
                                            <button
                                                className="actionbutton onethird no-image right-clearance-small"
                                                onClick={() =>
                                                    this.chooseFont(
                                                        this.props
                                                            .tableHeadFont,
                                                        "tableHeadFont",
                                                    )
                                                }>
                                                <span>Table Heading</span>
                                            </button>

                                            <span className="font-example table-head-font">
                                                {this.props.tableHeadFont?.name}{" "}
                                                {this.props.tableHeadFont?.size}{" "}
                                                {this.props.tableHeadFont
                                                    ?.weight === 500
                                                    ? ""
                                                    : "Bold"}{" "}
                                                {this.props.tableHeadFont
                                                    ?.decoration === "normal"
                                                    ? ""
                                                    : "Italic"}
                                            </span>
                                        </div>
                                        <div className="top-clearance-small">
                                            <button
                                                className="actionbutton onethird no-image right-clearance-small"
                                                onClick={() =>
                                                    this.chooseFont(
                                                        this.props.tableFont,
                                                        "tableFont",
                                                    )
                                                }>
                                                <span>Table</span>
                                            </button>
                                            <span className="font-example table-body-font">
                                                {this.props.tableFont?.name}{" "}
                                                {this.props.tableFont?.size}{" "}
                                                {this.props.tableFont
                                                    ?.weight === 500
                                                    ? ""
                                                    : "Bold"}{" "}
                                                {this.props.tableFont
                                                    ?.decoration === "normal"
                                                    ? ""
                                                    : "Italic"}
                                            </span>
                                        </div>
                                    </div>
                                </Panel>
                                <Panel className="buffer top-clearance-small">
                                    <h3>Export Only</h3>

                                    <div className="top-clearance-small full">
                                        <div className="flex-row">
                                            <Panel className="buffer">
                                                <h3>Page Orientation</h3>
                                                <div className="top-clearance-small">
                                                    <ul className="no-style">
                                                        <li>
                                                            <label className="radio">
                                                                <input
                                                                    type="radio"
                                                                    name="orientation"
                                                                    value={0}
                                                                    checked={
                                                                        (this
                                                                            .props
                                                                            .pageOrientation ??
                                                                            0) ===
                                                                        0
                                                                    }
                                                                    onChange={() =>
                                                                        this.addOrientationChanged(
                                                                            0,
                                                                        )
                                                                    }
                                                                />
                                                                <span className="checkmark"></span>
                                                                <span className="text">
                                                                    Portrait
                                                                </span>
                                                            </label>
                                                        </li>
                                                        <li>
                                                            <label className="radio">
                                                                <input
                                                                    type="radio"
                                                                    name="orientation"
                                                                    value={1}
                                                                    checked={
                                                                        this
                                                                            .props
                                                                            .pageOrientation ===
                                                                        1
                                                                    }
                                                                    onChange={() =>
                                                                        this.addOrientationChanged(
                                                                            1,
                                                                        )
                                                                    }
                                                                />
                                                                <span className="checkmark"></span>
                                                                <span className="text">
                                                                    Landscape
                                                                </span>
                                                            </label>
                                                        </li>
                                                    </ul>
                                                </div>
                                            </Panel>

                                            <Panel className="buffer col left-clearance-small">
                                                <h3>Extra Margin (mm)</h3>
                                                <div className="flex-column">
                                                    <div className="flex-row">
                                                        <label className="print-margin col whole half text-right top-clearance-small">
                                                            <span>Top</span>
                                                            <input
                                                                type="number"
                                                                step="1"
                                                                min="0"
                                                                max="100"
                                                                value={
                                                                    this.props
                                                                        .margin
                                                                        .top ??
                                                                    0
                                                                }
                                                                onChange={ev =>
                                                                    this.marginTopChanged(
                                                                        Number(
                                                                            ev
                                                                                .target
                                                                                .value,
                                                                        ),
                                                                    )
                                                                }
                                                            />
                                                        </label>
                                                        <label className="print-margin col whole half text-right top-clearance-small">
                                                            <span>Left</span>
                                                            <input
                                                                type="number"
                                                                step="1"
                                                                min="0"
                                                                max="100"
                                                                value={
                                                                    this.props
                                                                        .margin
                                                                        .left ??
                                                                    0
                                                                }
                                                                onChange={ev =>
                                                                    this.marginLeftChanged(
                                                                        Number(
                                                                            ev
                                                                                .target
                                                                                .value,
                                                                        ),
                                                                    )
                                                                }
                                                            />
                                                        </label>
                                                    </div>
                                                    <div className="flex-row">
                                                        <label className="print-margin col whole half text-right top-clearance-small">
                                                            <span>Bottom</span>
                                                            <input
                                                                type="number"
                                                                step="1"
                                                                min="0"
                                                                max="100"
                                                                value={
                                                                    this.props
                                                                        .margin
                                                                        .bottom ??
                                                                    0
                                                                }
                                                                onChange={ev =>
                                                                    this.marginBottomChanged(
                                                                        Number(
                                                                            ev
                                                                                .target
                                                                                .value,
                                                                        ),
                                                                    )
                                                                }
                                                            />
                                                        </label>
                                                        <label className="print-margin col whole half text-right top-clearance-small">
                                                            <span>Right</span>
                                                            <input
                                                                type="number"
                                                                step="1"
                                                                min="0"
                                                                max="100"
                                                                value={
                                                                    this.props
                                                                        .margin
                                                                        ?.right ??
                                                                    0
                                                                }
                                                                onChange={ev =>
                                                                    this.marginRightChanged(
                                                                        Number(
                                                                            ev
                                                                                .target
                                                                                .value,
                                                                        ),
                                                                    )
                                                                }
                                                            />
                                                        </label>
                                                    </div>
                                                </div>
                                            </Panel>
                                        </div>
                                        <Panel className="buffer top-clearance-small full">
                                            <h3>Format Options</h3>

                                            <div>
                                                <Checkbox
                                                    className="hasLabel d-iblock"
                                                    label="Add extra columns:"
                                                    value={
                                                        this.props.addColumns
                                                    }
                                                    onChange={() =>
                                                        this.addColsChanged()
                                                    }></Checkbox>
                                                <label className="additional-cell horizontal-buffer-small">
                                                    <input
                                                        type="number"
                                                        step="1"
                                                        min="0"
                                                        max="10"
                                                        value={
                                                            this.props.noColumns
                                                        }
                                                        disabled={
                                                            !this.props
                                                                .addColumns
                                                        }
                                                        onChange={ev =>
                                                            this.noColumnsChanged(
                                                                Number(
                                                                    ev.target
                                                                        .value,
                                                                ),
                                                            )
                                                        }
                                                    />
                                                    <span>column</span>
                                                </label>
                                                <label className="additional-cell">
                                                    width
                                                    <input
                                                        type="number"
                                                        step=".1"
                                                        min="0"
                                                        max="50"
                                                        value={
                                                            this.props.colWidth
                                                        }
                                                        disabled={
                                                            !this.props
                                                                .addColumns
                                                        }
                                                        onChange={ev =>
                                                            this.colWidthChanged(
                                                                Number(
                                                                    ev.target
                                                                        .value,
                                                                ),
                                                            )
                                                        }
                                                    />
                                                </label>
                                                <br />
                                                <Checkbox
                                                    className="hasLabel d-iblock"
                                                    label="Export each table on a
                                                        separate page/sheet"
                                                    value={
                                                        this.props.separatePages
                                                    }
                                                    onChange={() =>
                                                        this.separatePagesChanged()
                                                    }></Checkbox>
                                            </div>
                                        </Panel>
                                    </div>
                                </Panel>
                            </div>
                            <div className="col-pane onethird left-clearance-small flex-column">
                                <Panel className="col-pane buffer">
                                    <h3>Print</h3>

                                    <div className="top-clearance-small full">
                                        <button
                                            className="full"
                                            data-tip="View print preview"
                                            onClick={this.preview.bind(this)}
                                            disabled={this.state.isLoading}>
                                            <i>find_in_page</i>
                                            <span>Preview</span>
                                        </button>
                                    </div>
                                </Panel>

                                <Panel className="col buffer top-clearance-small">
                                    <h3>Export</h3>

                                    <div className="top-clearance-small full">
                                        <button
                                            className="full top-clearance-small"
                                            data-tip="Export to Excel"
                                            onClick={this.exportXLS.bind(this)}
                                            disabled={
                                                this.props.disableXLS ||
                                                this.state.isLoading
                                            }>
                                            <img
                                                src={xlsIcon}
                                                alt="Excel"
                                            />
                                            <span>Excel</span>
                                        </button>

                                        <button
                                            className="full top-clearance"
                                            data-tip="Export to Word"
                                            onClick={this.exportDOC.bind(this)}
                                            disabled={
                                                this.props.disableDOC ||
                                                this.state.isLoading
                                            }>
                                            <img
                                                src={docIcon}
                                                alt="Word"
                                            />
                                            <span>Word</span>
                                        </button>

                                        <button
                                            className="full top-clearance"
                                            data-tip="Export to PDF"
                                            onClick={this.exportPDF.bind(this)}
                                            disabled={
                                                this.props.disablePDF ||
                                                this.state.isLoading
                                            }>
                                            <img
                                                src={pdfIcon}
                                                alt="PDF"
                                            />
                                            <span>PDF</span>
                                        </button>
                                    </div>
                                </Panel>
                            </div>
                        </div>
                    </DialogContent>
                    <DialogFooter>
                        <DialogClose
                            onClick={this.props.printClosed}></DialogClose>
                        <DialogOK
                            onClick={this.printPDF.bind(this)}
                            icon="print"
                            text="Print"></DialogOK>
                    </DialogFooter>
                </Dialog>

                <FontMenu
                    open={this.state.fontMenuOpen}
                    font={this.state.editingFont}
                    fontsClosed={this.closeFontMenu.bind(this)}
                    fontsChanged={this.updateFont.bind(this)}></FontMenu>

                <PreviewDialog
                    open={this.state.previewDialogOpen}
                    previewData={this.state.previewData}
                    previewClosed={this.closePreview.bind(
                        this,
                    )}></PreviewDialog>
            </>
        );
    }
}

const mapStateToProps = (state: IAppState) => {
    const { ui } = state.db;
    return {
        disableDOC: ui.disableDOC,
        disableXLS: ui.disableXLS,
        disablePDF: ui.disablePDF,
        schoolFont: ui.printConfig.schoolFont,
        tableHeadFont: ui.printConfig.tableHeadFont,
        tableFont: ui.printConfig.tableFont,
        pageOrientation: ui.printConfig.pageOrientation,
        margin: ui.printConfig.margin,
        addColumns: ui.printConfig.addColumns,
        noColumns: ui.printConfig.noColumns,
        colWidth: ui.printConfig.colWidth,
        separatePages: ui.printConfig.separatePages,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        setSchoolFont: (font: Font) => dispatch(setSchoolFont(font)),
        setTableHeadFont: (font: Font) => dispatch(setTableHeadFont(font)),
        setTableFont: (font: Font) => dispatch(setTableFont(font)),
        setMargin: (margin: Margin) => dispatch(setMargin(margin)),
        setAddColumns: (addColumns: boolean) =>
            dispatch(setAddColumns(addColumns)),
        setNoColumns: (noColumns: number) => dispatch(setNoColumns(noColumns)),
        setColWidth: (colWidth: number) => dispatch(setColWidth(colWidth)),
        setSeparatePages: (separatePages: boolean) =>
            dispatch(setSeparatePages(separatePages)),
        setPageOrientation: (orientation: number) =>
            dispatch(setPageOrientation(orientation)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(PrintMenu);
